import { balance, type Balance } from './balance';
import { effectiveStat, hpBasis } from './matchup';
import {
  fieldGoalDistance,
  fieldGoalOdds,
  kickerSlot,
  resolveFieldGoal,
  resolvePunt,
} from './kick';
import { resolvePlay, type PlayOutcome } from './play';
import { DEFAULT_PLAYBOOK, playById, type Playbook } from './playbook';
import { DEFENSE_SLOTS, OFFENSE_SLOTS, SLOT_POSITION } from './playdef';
import { createRng, type Rng } from './rng';
import { afterSnap, applyWindow, hasWindow, itemOf, type Side } from './subs';
import type {
  Combatant,
  DefenseDecision,
  DefenseSlot,
  FourthDownChoice,
  Game,
  GameEvent,
  GameState,
  OffenseDecision,
  OffenseSlot,
  PossessionReason,
  ScoreKind,
  Situation,
  SnapDecision,
  SubWindow,
  Team,
  TeamId,
} from './types';

/**
 * The game loop: a whole game from a seed, two teams, and the coaches' decisions, to the final
 * state and an ordered game log. Every roll comes from the one game rng, in order, so a game
 * stored as seed + decisions replays byte for byte. The sim never asks for input: a call with no
 * decision takes the playbook default. See docs/design-plan.md, "How a game runs".
 *
 * Phases: a kickoff puts the receiving team at its 25 (no return play in v1); scrimmage calls run
 * snaps, punts, and field goals; a touchdown earns a conversion call (kick, or one snap from the
 * 3). A drive ends on a score, a turnover (interception, fumble lost, downs), a punt, a missed
 * field goal, or a safety; possession flips and stat stages reset. Quarters end at 0:00 once the
 * play (and any try) is over; halftime restores HP and clears stages; a tie after regulation goes
 * to overtime, which always ends with a winner. Every number is in balance.json `football`.
 */

export interface GameOptions {
  playbook?: Playbook;
  balance?: Balance;
  /**
   * Stop before asking for this call (its index in the decision list) and hand back the call as
   * `pending`: how a client pauses the game for a person to decide. Absent: play to the final.
   */
  untilCall?: number;
}

export interface GameResult {
  game: Game;
  events: GameEvent[];
  /** The call the game stopped before (`untilCall`); absent once the game is over. */
  pending?: CallView;
}

/** What a coach is told when asked for a call. The game is the state before the call. */
export interface CallView {
  game: Readonly<Game>;
  /** Index of this call in the game's decision list. */
  call: number;
  kind: 'scrimmage' | 'conversion';
  playbook: Playbook;
}

/** What a coach is told in its sub window: the call, and whose window it is. */
export interface WindowView extends CallView {
  side: Side;
  team: TeamId;
}

/**
 * Anything that makes calls: the client's player, the AI, the placeholder coach. `subs` is the sub
 * window (slice 9), asked after the offense's call unless it is hurry-up: the offense's window,
 * then the defense's, whose view already shows the offense's new personnel (never its play or
 * spends), then the defense's call. A coach may also put window fields in its call directly.
 */
export interface Coach {
  offense(view: CallView): OffenseDecision;
  defense(view: CallView): DefenseDecision;
  subs?(view: WindowView): SubWindow<string>;
}

/** Where the engine gets each call's decisions. */
export type DecisionSource = (view: CallView) => SnapDecision;

const other = (t: TeamId): TeamId => (t === 'A' ? 'B' : 'A');

type StopReason = 'incomplete' | 'out-of-bounds' | 'score' | 'turnover' | 'kick';

/** A fresh game before the opening kickoff, `receiving` having won the toss. */
export function newGame(
  teams: Record<TeamId, Team>,
  receiving: TeamId,
  b: Balance = balance,
): Game {
  for (const id of ['A', 'B'] as const) validateTeam(teams[id], id, b);
  return {
    teams,
    score: { A: 0, B: 0 },
    quarter: 1,
    clock: b.football.quarterSeconds,
    clockRunning: false,
    possession: receiving,
    ballOn: b.football.kickoffStartYardLine,
    down: 1,
    toGo: b.football.yardsToGo,
    openingReceiver: receiving,
    phase: 'kickoff',
    snaps: 0,
    calls: 0,
  };
}

function validateTeam(t: Team, id: TeamId, b: Balance): void {
  if (t.id !== id) throw new Error(`team ${id} has id "${t.id}"`);
  const n = t.roster.length;
  if (n < b.roster.starters || n > b.roster.starters + b.roster.bench) {
    throw new Error(
      `team ${id} dresses ${n}; a roster is ${b.roster.starters} to ${b.roster.starters + b.roster.bench}`,
    );
  }
  const bag = t.bag ?? [];
  if (bag.length > b.status.itemBagPerGame) {
    throw new Error(
      `team ${id} packs ${bag.length} bag items; a bag holds ${b.status.itemBagPerGame}`,
    );
  }
  for (const item of bag) {
    if (itemOf(item, b)?.slot !== 'bag') throw new Error(`team ${id}: ${item} is not a bag item`);
  }
  for (const [chart, slots] of [
    ['offense', OFFENSE_SLOTS],
    ['defense', DEFENSE_SLOTS],
  ] as const) {
    const idx = slots.map((s) => (t[chart] as Record<string, number>)[s]);
    if (idx.some((i) => i === undefined || !Number.isInteger(i) || i < 0 || i >= n)) {
      throw new Error(`team ${id}'s ${chart} chart points outside its roster`);
    }
    if (new Set(idx).size !== idx.length) {
      throw new Error(`team ${id}'s ${chart} chart lists one Pokémon twice`);
    }
  }
}

/** The 4th-down call when the coach makes none (balance.football.fourthDown). */
export function defaultFourthDown(game: Readonly<Game>, b: Balance): FourthDownChoice {
  const team = game.teams[game.possession];
  const lineup = lineupOf(team, team.offense);
  const spa = effectiveStat(lineup[kickerSlot(lineup, b)], 'spa', b);
  const rule = b.football.fourthDown;
  if (fieldGoalOdds(spa, fieldGoalDistance(game.ballOn, b), false, b) >= rule.minFieldGoalOdds) {
    return 'field-goal';
  }
  if (game.ot) return 'go';
  return game.toGo <= rule.goMaxToGo && game.ballOn >= rule.goMinBallOn ? 'go' : 'punt';
}

/** Play a whole game from a seed and a decision list (one entry per call; missing = defaults). */
export function playGame(
  seed: number,
  teams: Record<TeamId, Team>,
  decisions: readonly SnapDecision[],
  opts: GameOptions = {},
): GameResult {
  const rng = createRng(seed);
  const receiving: TeamId = rng.chance(0.5) ? 'A' : 'B';
  const b = opts.balance ?? balance;
  const start = newGame(teams, receiving, b);
  const result = runGame(start, rng, ({ call }) => decisions[call] ?? {}, { ...opts, balance: b });
  result.events.unshift({ type: 'coin-toss', receiving });
  return result;
}

/**
 * Play a whole game asking two coaches for every call, and return the decisions they made: that
 * list through `playGame` with the same seed replays this game exactly.
 */
export function playGameWith(
  seed: number,
  teams: Record<TeamId, Team>,
  coaches: Record<TeamId, Coach>,
  opts: GameOptions = {},
): GameResult & { decisions: SnapDecision[] } {
  const b = opts.balance ?? balance;
  const decisions: SnapDecision[] = [];
  const source: DecisionSource = (view) => {
    const o = view.game.possession;
    const { offense, game } = callOffense(view, coaches[o], b);
    const d = callDefense(view, game, offense, coaches[other(o)], b);
    decisions.push(d);
    return d;
  };
  const rng = createRng(seed);
  const receiving: TeamId = rng.chance(0.5) ? 'A' : 'B';
  const result = runGame(newGame(teams, receiving, b), rng, source, { ...opts, balance: b });
  result.events.unshift({ type: 'coin-toss', receiving });
  return { ...result, decisions };
}

/**
 * Play a game from a seed and the decisions so far, stopping before the next call: the events up
 * to there and the call that is `pending` (none once the game is over). A client asks its player
 * for that call, appends it, and plays again; the server replays the same list with `playGame`.
 */
export function playUntil(
  seed: number,
  teams: Record<TeamId, Team>,
  decisions: readonly SnapDecision[],
  opts: GameOptions = {},
): GameResult {
  return playGame(seed, teams, decisions, { ...opts, untilCall: decisions.length });
}

/**
 * The first half of a call: the offense's coach makes its call, then its sub window (none on
 * hurry-up). Returns the offense's decision and the game as the defense will see it: the offense's
 * new personnel, never its play or spends. `playGameWith` and the client both build calls this way.
 */
export function callOffense(
  view: CallView,
  coach: Coach,
  b: Balance = balance,
): { offense: OffenseDecision; game: Game } {
  const o = view.game.possession;
  const offense: OffenseDecision = { ...coach.offense(view) };
  let game = view.game as Game;
  if (!(offense.tempo === 'hurry-up' && b.subs.hurryUpSkipsWindows)) {
    const ow = coach.subs?.({ ...view, side: 'offense', team: o });
    if (ow && hasWindow(ow)) {
      Object.assign(offense, windowFields<OffenseSlot>(ow));
      game = withWindow(game, o, 'offense', ow, b);
    }
  }
  return { offense, game };
}

/**
 * The second half of a call: the defense's sub window (none on hurry-up), then its call, both on
 * `game` as `callOffense` left it. Returns the call's whole decision.
 */
export function callDefense(
  view: CallView,
  game: Readonly<Game>,
  offense: OffenseDecision,
  coach: Coach,
  b: Balance = balance,
): SnapDecision {
  const dt = other(view.game.possession);
  let seen = game;
  let defenseWindow: SubWindow<string> | undefined;
  if (!(offense.tempo === 'hurry-up' && b.subs.hurryUpSkipsWindows)) {
    // The defense sees the offense's personnel, never its call.
    const dw = coach.subs?.({ ...view, game: seen, side: 'defense', team: dt });
    if (dw && hasWindow(dw)) {
      defenseWindow = dw;
      seen = withWindow(seen, dt, 'defense', dw, b);
    }
  }
  const defense: DefenseDecision = {
    ...coach.defense({ ...view, game: seen }),
    ...(defenseWindow ? windowFields<DefenseSlot>(defenseWindow) : {}),
  };
  return { offense, defense };
}

/** Only the window's non-empty fields, for the decision record. */
function windowFields<S extends OffenseSlot | DefenseSlot>(w: SubWindow<string>): SubWindow<S> {
  return {
    ...(w.subs?.length ? { subs: w.subs as NonNullable<SubWindow<S>['subs']> } : {}),
    ...(w.rest?.length ? { rest: w.rest } : {}),
    ...(w.bag?.length ? { bag: w.bag } : {}),
  };
}

/** `game` with one team's window applied: what the next coach in line sees. */
function withWindow(
  game: Readonly<Game>,
  team: TeamId,
  side: Side,
  w: SubWindow<string>,
  b: Balance,
): Game {
  const t = applyWindow(game.teams[team], side, w, b).team;
  return { ...game, teams: { ...game.teams, [team]: t } };
}

/** A chart's Pokémon, each with `position` set to the slot it plays (position fit reads it). */
export function lineupOf<S extends string>(
  team: Team,
  chart: Record<S, number>,
): Record<S, Combatant> {
  const out = {} as Record<S, Combatant>;
  for (const s of Object.keys(chart) as S[]) {
    const c = team.roster[chart[s]] as Combatant;
    const position = SLOT_POSITION[s as OffenseSlot | DefenseSlot];
    out[s] = c.position === position ? c : { ...c, position };
  }
  return out;
}

/**
 * The engine: run `start` to the final whistle, drawing from `rng` and asking `next` for each
 * call. `start` is not mutated. Tests start it from hand-built games.
 */
export function runGame(
  start: Game,
  rng: Rng,
  next: DecisionSource,
  opts: GameOptions & { balance: Balance },
): GameResult {
  const b = opts.balance;
  const playbook = opts.playbook ?? DEFAULT_PLAYBOOK;
  const F = b.football;
  const g: Game = {
    ...start,
    teams: {
      A: { ...start.teams.A, roster: [...start.teams.A.roster] },
      B: { ...start.teams.B, roster: [...start.teams.B.roster] },
    },
    score: { ...start.score },
    ...(start.ot ? { ot: { ...start.ot } } : {}),
  };
  const events: GameEvent[] = [];
  let pending: CallView | undefined;
  /** `untilCall` reached: hand this call back instead of asking for it. */
  const stopAt = (kind: CallView['kind']): boolean => {
    if (opts.untilCall === undefined || g.calls < opts.untilCall) return false;
    pending = { game: g, call: g.calls, kind, playbook };
    return true;
  };
  const regulation = () => g.quarter <= F.quarters;
  const firstDown = (ballOn: number) => Math.min(F.yardsToGo, 100 - ballOn);

  const situation = (): Situation => ({
    quarter: g.quarter,
    clock: g.clock,
    score: { ...g.score },
    offense: g.possession,
    ballOn: g.ballOn,
    down: g.down,
    toGo: g.toGo,
  });

  /** Every Pokémon on both rosters, field and bench, through `f`; `f` returns a new combatant. */
  const everyone = (f: (c: Combatant) => Combatant) => {
    for (const id of ['A', 'B'] as const) g.teams[id].roster = g.teams[id].roster.map(f);
  };
  const resetStages = () => {
    if (!b.stages.resetOnChangeOfPossession) return;
    everyone((c) => (Object.keys(c.stages).length === 0 ? c : { ...c, stages: {} }));
  };

  /**
   * A snap happened: count it, run the clock, and for everyone count status down, heal the bench,
   * and run Leftovers on the field (subs.ts afterSnap). The field is the charts that just played.
   */
  const tick = (timed: boolean) => {
    g.snaps += 1;
    if (g.snaps > F.maxSnapsPerGame) {
      throw new Error(`game passed ${F.maxSnapsPerGame} snaps: the game loop is not ending`);
    }
    if (timed && regulation()) g.clock = Math.max(0, g.clock - F.clock.playSeconds);
    for (const id of ['A', 'B'] as const) {
      const team = g.teams[id];
      const field = new Set(Object.values(id === g.possession ? team.offense : team.defense));
      team.roster = team.roster.map((c, i) => afterSnap(c, field.has(i), b));
    }
  };

  /** The call's sub windows, the offense's then the defense's; hurry-up skips both. */
  const windows = (call: number, d: SnapDecision) => {
    const hurry = d.offense?.tempo === 'hurry-up' && b.subs.hurryUpSkipsWindows;
    const seats: [Side, TeamId, SubWindow<string> | undefined][] = [
      ['offense', g.possession, d.offense],
      ['defense', other(g.possession), d.defense],
    ];
    for (const [side, id, w] of seats) {
      if (!w || !hasWindow(w)) continue;
      if (hurry) {
        throw new Error(`call ${call}: hurry-up skips the sub windows, but the ${side} used one`);
      }
      const r = applyWindow(g.teams[id], side, w, b);
      g.teams[id] = r.team;
      events.push(...r.events);
    }
  };

  const lineups = () => {
    const o = g.teams[g.possession];
    const d = g.teams[other(g.possession)];
    return { o, d, offense: lineupOf(o, o.offense), defense: lineupOf(d, d.defense) };
  };

  const writeBack = (
    team: Team,
    chart: Record<string, number>,
    lineup: Record<string, Combatant>,
  ) => {
    for (const [s, i] of Object.entries(chart)) team.roster[i] = lineup[s] as Combatant;
  };

  const addScore = (team: TeamId, kind: ScoreKind) => {
    const points = {
      touchdown: F.scoring.touchdown,
      'extra-point': F.scoring.extraPointKick,
      'two-point': F.scoring.twoPoint,
      'field-goal': F.scoring.fieldGoal,
      safety: F.scoring.safety,
    }[kind];
    g.score[team] += points;
    events.push({ type: 'score', team, kind, points, score: { ...g.score } });
  };

  const finish = (winner: TeamId) => {
    g.phase = 'final';
    g.winner = winner;
    events.push({ type: 'final', winner, score: { ...g.score }, snaps: g.snaps });
  };
  const leader = (): TeamId | undefined =>
    g.score.A === g.score.B ? undefined : g.score.A > g.score.B ? 'A' : 'B';

  /** Where an overtime possession starts: the opponent's 25, or its 10 from the 3rd period on. */
  const overtimeBallOn = (period: number) =>
    100 -
    (period >= F.overtime.shortFieldFromPeriod
      ? F.overtime.shortFieldYardLine
      : F.overtime.possessionStartYardLine);

  const startOvertime = (period: number) => {
    const first = period % 2 === 1 ? other(g.openingReceiver) : g.openingReceiver;
    g.quarter = F.quarters + period;
    g.clock = 0;
    g.clockRunning = false;
    g.ot = { period, possession: 1 };
    events.push({ type: 'overtime', period, ballOn: overtimeBallOn(period), first });
    givePossession(first, overtimeBallOn(period), 'overtime');
  };

  /** Overtime: the possession under way is over (any way at all). */
  const overtimePossessionOver = () => {
    const ot = g.ot as NonNullable<Game['ot']>;
    if (ot.possession === 1) {
      ot.possession = 2;
      givePossession(other(g.possession), overtimeBallOn(ot.period), 'overtime');
      return;
    }
    const w = leader();
    if (w) finish(w);
    else startOvertime(ot.period + 1);
  };

  function givePossession(team: TeamId, ballOn: number, reason: PossessionReason) {
    g.possession = team;
    g.ballOn = ballOn;
    g.down = 1;
    g.toGo = firstDown(ballOn);
    g.phase = 'scrimmage';
    g.clockRunning = false;
    resetStages();
    events.push({ type: 'possession', team, ballOn, reason });
  }

  /** A drive is over and the other team gets the ball at `ballOn` (theirs). */
  const turnover = (ballOn: number, reason: PossessionReason) => {
    if (g.ot) overtimePossessionOver();
    else givePossession(other(g.possession), ballOn, reason);
  };

  /** After a score: `receiving` gets the kickoff (the other team; after a safety, the scorer). */
  const afterScore = (receiving: TeamId) => {
    if (g.ot) {
      overtimePossessionOver();
      return;
    }
    g.phase = 'kickoff';
    g.possession = receiving;
    g.clockRunning = false;
  };

  /** Overtime scoring that ends the game on the spot: sudden death, or a lead taken on the last possession. */
  const overtimeWalkOff = (): boolean => {
    if (!g.ot) return false;
    const w = leader();
    const sudden = g.ot.period > F.overtime.suddenDeathAfterPeriod;
    if (w && (sudden || (g.ot.possession === 2 && w === g.possession))) {
      finish(w);
      return true;
    }
    return false;
  };

  const endQuarter = () => {
    events.push({ type: 'quarter-end', quarter: g.quarter, score: { ...g.score } });
    if (g.quarter === F.quarters) {
      const w = leader();
      if (w) finish(w);
      else startOvertime(1);
      return;
    }
    g.quarter += 1;
    g.clock = F.quarterSeconds;
    g.clockRunning = false;
    if (g.quarter === F.quarters / 2 + 1) {
      everyone((c) => ({
        ...c,
        hp: Math.min(c.stats.hp, c.hp + Math.round(hpBasis(c.stats.hp, b) * b.hp.halftimeRestore)),
        stages: {},
      }));
      events.push({ type: 'halftime', score: { ...g.score } });
      g.phase = 'kickoff';
      g.possession = other(g.openingReceiver);
    }
  };

  /** After any snap or kick: end the quarter if the clock is out and no try is pending. */
  const checkClock = () => {
    if (g.phase !== 'final' && g.phase !== 'conversion' && regulation() && g.clock === 0)
      endQuarter();
  };

  const snap = (call: number, d: SnapDecision, kind: 'scrimmage' | 'two-point') => {
    const { o, d: dt, offense, defense } = lineups();
    const offensePlay = playById(playbook.offense, d.offense?.playId);
    const defensePlay = playById(playbook.defense, d.defense?.playId);
    const before: GameState = {
      offense: { lineup: offense },
      defense: { lineup: defense },
      ballOn: g.ballOn,
      down: g.down,
      toGo: g.toGo,
    };
    const at = situation();
    const outcome = resolvePlay(
      before,
      {
        offense: offensePlay,
        defense: defensePlay,
        ...(d.offense?.moves ? { offenseMoves: d.offense.moves } : {}),
        ...(d.defense?.moves ? { defenseMoves: d.defense.moves } : {}),
        tempo: d.offense?.tempo ?? 'normal',
      },
      rng,
      b,
    );
    writeBack(o, o.offense, outcome.state.offense.lineup);
    writeBack(dt, dt.defense, outcome.state.defense.lineup);
    const stop = stopReason(outcome, kind);
    events.push({
      type: 'snap',
      at,
      call,
      kind,
      offensePlay: offensePlay.id,
      defensePlay: defensePlay.id,
      before,
      outcome,
      clockStopped: stop !== null && F.clockStopsOn.includes(stop),
    });
    tick(kind === 'scrimmage');
    return { outcome, stop };
  };

  const scrimmage = () => {
    if (stopAt('scrimmage')) return;
    const call = g.calls++;
    const d = next({ game: g, call, kind: 'scrimmage', playbook });
    windows(call, d);
    if (regulation() && g.clockRunning) {
      g.clock = Math.max(
        0,
        g.clock -
          (d.offense?.tempo === 'hurry-up' ? F.clock.hurryUpRunoffSeconds : F.clock.runoffSeconds),
      );
      if (g.clock === 0) {
        endQuarter();
        return;
      }
    }
    if (g.down === F.downs) {
      const choice = d.offense?.fourthDown ?? defaultFourthDown(g, b);
      if (choice === 'punt') return punt(call);
      if (choice === 'field-goal') return fieldGoal(call);
    }
    const { outcome, stop } = snap(call, d, 'scrimmage');
    g.clockRunning = stop === null || !F.clockStopsOn.includes(stop);
    const team = g.possession;
    switch (outcome.result) {
      case 'touchdown':
        addScore(team, 'touchdown');
        if (!overtimeWalkOff()) g.phase = 'conversion';
        break;
      case 'safety':
        addScore(other(team), 'safety');
        if (!overtimeWalkOff()) afterScore(other(team));
        break;
      case 'interception':
      case 'fumble': {
        const x = spotOf(outcome);
        turnover(x >= 100 ? F.touchbackYardLine : 100 - x, outcome.result);
        break;
      }
      default:
        if (outcome.turnoverOnDowns) {
          turnover(100 - outcome.state.ballOn, 'downs');
        } else {
          g.ballOn = outcome.state.ballOn;
          g.down = outcome.state.down;
          g.toGo = outcome.state.toGo;
        }
    }
    checkClock();
  };

  const punt = (call: number) => {
    const { offense, defense } = lineups();
    const at = situation();
    const k = resolvePunt(offense, defense, g.ballOn, rng, b);
    events.push({ type: 'punt', at, call, ...k });
    tick(true);
    const ballOn = k.blocked
      ? 100 - g.ballOn
      : k.touchback
        ? F.touchbackYardLine
        : 100 - (g.ballOn + k.yards);
    turnover(ballOn, 'punt');
    checkClock();
  };

  const fieldGoal = (call: number) => {
    const { offense, defense } = lineups();
    const at = situation();
    const k = resolveFieldGoal(offense, defense, g.ballOn, rng, b);
    events.push({ type: 'field-goal', at, call, ...k });
    tick(true);
    if (k.good) {
      addScore(g.possession, 'field-goal');
      if (!overtimeWalkOff()) afterScore(other(g.possession));
    } else {
      turnover(Math.max(F.missedFieldGoalYardLine, 100 - g.ballOn), 'missed-field-goal');
    }
    checkClock();
  };

  const conversion = () => {
    if (stopAt('conversion')) return;
    const call = g.calls++;
    const d = next({ game: g, call, kind: 'conversion', playbook });
    windows(call, d);
    const team = g.possession;
    g.down = 1;
    if ((d.offense?.conversion ?? 'kick') === 'kick') {
      g.ballOn = 100 - F.extraPointYardLine;
      g.toGo = F.extraPointYardLine;
      const { offense, defense } = lineups();
      const at = situation();
      const k = resolveFieldGoal(offense, defense, g.ballOn, rng, b);
      events.push({ type: 'extra-point', at, call, ...k });
      tick(false);
      if (k.good) addScore(team, 'extra-point');
    } else {
      g.ballOn = 100 - F.twoPointFromYardLine;
      g.toGo = F.twoPointFromYardLine;
      const { outcome } = snap(call, d, 'two-point');
      if (outcome.result === 'touchdown') addScore(team, 'two-point');
    }
    if (overtimeWalkOff()) return;
    afterScore(other(team));
    checkClock();
  };

  const kickoff = () => {
    const receiving = g.possession;
    events.push({
      type: 'kickoff',
      kicking: other(receiving),
      receiving,
      ballOn: F.kickoffStartYardLine,
    });
    givePossession(receiving, F.kickoffStartYardLine, 'kickoff');
  };

  while (g.phase !== 'final' && pending === undefined) {
    if (g.phase === 'kickoff') kickoff();
    else if (g.phase === 'conversion') conversion();
    else scrimmage();
  }
  return { game: g, events, ...(pending ? { pending } : {}) };
}

/** Why a snap stopped the clock, or null if it left it running. */
function stopReason(o: PlayOutcome, kind: 'scrimmage' | 'two-point'): StopReason | null {
  if (kind === 'two-point') return 'score';
  switch (o.result) {
    case 'incomplete':
      return o.turnoverOnDowns ? 'turnover' : 'incomplete';
    case 'touchdown':
    case 'safety':
      return 'score';
    case 'interception':
    case 'fumble':
      return 'turnover';
    default:
      if (o.turnoverOnDowns) return 'turnover';
      return o.outOfBounds ? 'out-of-bounds' : null;
  }
}

/** Where a turnover happened, in yards from the offense's goal line. */
function spotOf(o: PlayOutcome): number {
  for (const e of o.events) {
    if (e.type === 'interception' || e.type === 'fumble') return e.at[0];
  }
  throw new Error(`a ${o.result} with no spot in its events`);
}
